import {
  AccommodationArrangedStatusEnum,
  EventInfoStatus,
  IStudentRow,
  RecordedInterviewSystemStatus,
} from 'api-paths-cas';
import { parse as parseObjectToCsv } from 'json2csv';
import { getInterviewInfoStatusString, getRecordedInterviewInfoStatusString } from '../formatting';
import { getNotArrangedAccommodationTypeStatus } from '../helpers';
import { sortMaturity } from '../sortStudents';
import { UserForExport } from './types';
import { getDaysInCurrentStage } from '../stages/getDaysInTheCurrentStage';
import { getTags } from '../tags/getTags';
import { formatDateSafe } from '../dateHelpers';

export const serializeStudentForExport = (student: IStudentRow, isPathway: boolean): UserForExport => {
  const fundsMaturity = student.fundsMatured?.length
    ? sortMaturity(student.fundsMatured)
        .map(item => item.value)
        .join('; ')
    : '';
  const travelDate = student.travelBookedDate || student.travelArrivalDate;

  const tags = getTags(student, isPathway);

  const pathwayUni = student.uni.split(' - ')[1];
  const daysInCurrentStage = getDaysInCurrentStage(student);

  return {
    ...(pathwayUni ? { University: pathwayUni } : {}),
    'STU No': student.student_number,
    'Offer ID': student.offerId,
    'First Name': student.given_name || '',
    'Last Name': student.surname || '',
    DOB: formatDateSafe(student.dob, 'DD MM YYYY'),
    CourseStartDate: student.startDate || '',
    Course: student.course || '',
    CourseFee: student.courseFee || 0,
    'Course Scholarships Applied': student.scholarshipsApplied || 0,
    'Course Fee After Scholarships': student.feeAfterScholarships || 0,
    Nationality: student.nationality || '',
    Country: student.country || '',
    'CAS Checklist completion %': student.questionComplete,
    'Documents outstanding': String(student.documentComplete).split(' / ').join('|'),
    'Interview Booking': getInterviewInfoStatusString(
      (student.calendlyInterviewsInfoStatus || EventInfoStatus.NONE) as EventInfoStatus,
      student.calendlyInterviewStartDate ? Number(student.calendlyInterviewStartDate) : undefined,
      student.calendlyInterviewEndDate ? Number(student.calendlyInterviewEndDate) : undefined,
    ),
    'Recorded Video Interview': getRecordedInterviewInfoStatusString(
      (student.recordedInterviewSystemStatus || RecordedInterviewSystemStatus.NONE) as RecordedInterviewSystemStatus,
      student.recordedInterviewTimestamp,
    ),
    'Funds maturity': fundsMaturity,
    Stage: student.status,
    'Phone Number': student.phone ? `'+${student.phone}` : '',
    'Agent company name': student.agentName ?? '',
    'Last Touch by uni': student.lastTouch,
    'Last Save by agent/student': student.lastTouchByStudentOrAgent
      ? new Date(student.lastTouchByStudentOrAgent).toUTCString().replaceAll(',', '')
      : 'None',
    Email: student.email || '',
    'Days in the current stage': daysInCurrentStage,
    'Uni Deposit Amount': (student.courseFeePaidToDate || 0).toString(),
    'Student Deposit Amount': student.studentDepositAmount.toString(),
    'Dependant Partner': setDependantPartnerValue(student),
    'Dependant Children': setDependantChildrenValue(student),
    'Visa Status': student.visaStatus,
    'Arrival Status': `${student.travelStatus}${travelDate && ` (${travelDate})`}`,
    Accommodation: `${student.accommodationArrangedStatus}${
      student.accommodationTypeStatus &&
      ` - ${
        student.accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED
          ? getNotArrangedAccommodationTypeStatus(student.accommodationTypeStatus).toLocaleLowerCase()
          : student.accommodationTypeStatus.toLocaleLowerCase()
      }`
    }`,
    Tags: tags,
    Services: (student.partners || []).join('; '),
    'Immigration Providers': (student.immigrationProviders || []).join('; '),
    'Digital Status Share Code': student.immigrationStatusShareCode,
  };
};

export const setDependantChildrenValue = (student: IStudentRow) => {
  if (!student.dependants) {
    return '';
  }

  if (student.dependants?.childrenNum) {
    return student.dependants.childrenNum === 1 ? 'Yes (1 child)' : `Yes (${student.dependants.childrenNum} children)`;
  }
  return 'No';
};

export const setDependantPartnerValue = (student: IStudentRow) => {
  if (!student.dependants) {
    return '';
  }
  return student.dependants?.partnerVisaStatus && student.dependants?.partnerVisaStatus === 'APPLYING_DEPENDANT'
    ? 'Yes'
    : 'No';
};

export const parseStudentsToCsvString = (students: Array<IStudentRow>, isPathway: boolean): string => {
  const handledStudents: UserForExport[] = students.map(student => {
    const studentExport = serializeStudentForExport(student, isPathway);
    clearCommas(studentExport);
    return studentExport;
  });

  return handledStudents.length ? parseObjectToCsv(handledStudents, { escapedQuote: '', quote: '' }) : '';
};

const clearCommas = (studentExport: UserForExport) => {
  Object.keys(studentExport).forEach(k => {
    const key = k as keyof UserForExport;
    if (typeof studentExport[key] === 'string') {
      (studentExport as any)[key] = (studentExport[key] as string)?.replaceAll(',', ';');
    }
  });
};
